import React from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useAuth } from "../../../auth";
import { Avatar } from "../../../../ui/components/Avatar";
import { Button } from "../../../../ui/components/Button";
import { Tabs } from "../../../../ui/components/Tabs";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import { FeatherHelpCircle } from "@subframe/core";
import { getStoredAccountingBackend } from "../../../../config/accounting";
import { TallyConnectorDownload } from "../../../admin/presentation/components/TallyConnectorDownload";

// Import section components
import { RecoSection } from "../../../reco";
import { DataPage } from "../../../data-upload";
import { InvoicesSection } from "../../../invoices";
import { BillsSection } from "../../../bills";
import { CreditNotesSection } from "../../../credit";
import { DebitNotesSection } from "../../../debit";
import { CashBankSection } from "../../../cash-and-bank";
import { JournalSection } from "../../../journals";
import QueriesSection from "../../../../shared/queries/presentation/pages/QueriesSection";
import ComplianceSection from "../../../../components/sections/ComplianceSection";
import Gstr2bSection from "../../../../components/sections/Gstr2bSection";
import { ApprovalsSection } from "../../../approvals";
import { ChecklistSection } from "../../../month-end-checklist";
import { InboxSection } from "../../../email-inbox";
import { MastersSection } from "../../../masters/presentation/components/MastersSection";

export type DashboardSection =
  | "reco"
  | "data"
  | "masters"
  | "invoices"
  | "bills"
  | "credit"
  | "debit"
  | "cash-bank"
  | "journal"
  | "queries"
  | "compliance"
  | "approvals"
  | "checklist"
  | "inbox"
  | "2b";

export const DashboardPage: React.FC = () => {
  const { company } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Get current active section from URL
  const getActiveSection = (): DashboardSection => {
    const path = location.pathname.split("/dashboard/")[1] || "data";
    return path.split("/")[0] as DashboardSection;
  };

  const activeSection = getActiveSection();

  // Handle tab navigation
  const handleTabClick = (section: DashboardSection): void => {
    navigate(`/dashboard/${section}`);
  };

  // Handle raise query button
  const handleRaiseQuery = (): void => {
    navigate("/dashboard/queries");
  };

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start">
        {/* Common Header Section */}
        <div className="flex w-full flex-col items-start gap-4 bg-default-background px-8 py-6">
          <div className="flex w-full flex-wrap items-start justify-between">
            <div className="flex items-center gap-4">
              <Avatar
                size="large"
                image="https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OHx8Y29tcGFueXxlbnwwfHwwfHx8MA%3D%3D"
              />
              <div className="flex flex-col items-start gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-heading-2 font-heading-2 text-default-font">
                    {company?.companyName || "Acme Corporation"}
                  </span>
                  {/* <Badge variant="warning">High Risk</Badge> */}
                </div>
                {/* <span className="text-body font-body text-subtext-color">
                  Client ID: {generateClientId(company?.companyName)}
                </span> */}
              </div>
            </div>
            <div className="flex items-center gap-4">
              {getStoredAccountingBackend() === "tally" ? <TallyConnectorDownload /> : null}
              <Button
                variant="neutral-tertiary"
                icon={<FeatherHelpCircle />}
                onClick={handleRaiseQuery}
              >
                Raise Query
              </Button>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex w-full items-start overflow-x-auto">
            <Tabs>
              <Tabs.Item
                active={activeSection === "reco"}
                onClick={() => handleTabClick("reco")}
              >
                Reco
              </Tabs.Item>
              <Tabs.Item
                active={activeSection === "data"}
                onClick={() => handleTabClick("data")}
              >
                Data
              </Tabs.Item>
              <Tabs.Item
                active={activeSection === "masters"}
                onClick={() => handleTabClick("masters")}
              >
                Masters
              </Tabs.Item>
              <Tabs.Item
                active={activeSection === "invoices"}
                onClick={() => handleTabClick("invoices")}
              >
                Customer Sales
              </Tabs.Item>
              <Tabs.Item
                active={activeSection === "bills"}
                onClick={() => handleTabClick("bills")}
              >
                Vendor Bills
              </Tabs.Item>
              <Tabs.Item
                active={activeSection === "credit"}
                onClick={() => handleTabClick("credit")}
              >
                Credit Notes
              </Tabs.Item>
              <Tabs.Item
                active={activeSection === "debit"}
                onClick={() => handleTabClick("debit")}
              >
                Debit Notes
              </Tabs.Item>
              <Tabs.Item
                active={activeSection === "cash-bank"}
                onClick={() => handleTabClick("cash-bank")}
              >
                Cash and Bank
              </Tabs.Item>
              <Tabs.Item
                active={activeSection === "journal"}
                onClick={() => handleTabClick("journal")}
              >
                Journal
              </Tabs.Item>
              <Tabs.Item
                active={activeSection === "queries"}
                onClick={() => handleTabClick("queries")}
              >
                Queries
              </Tabs.Item>
              <Tabs.Item
                active={activeSection === "compliance"}
                onClick={() => handleTabClick("compliance")}
              >
                Compliance
              </Tabs.Item>
              <Tabs.Item
                active={activeSection === "checklist"}
                onClick={() => handleTabClick("checklist")}
              >
                Check List
              </Tabs.Item>
              <Tabs.Item
                active={activeSection === "approvals"}
                onClick={() => handleTabClick("approvals")}
              >
                Approvals
              </Tabs.Item>
              <Tabs.Item
                active={activeSection === "inbox"}
                onClick={() => handleTabClick("inbox")}
              >
                Inbox
              </Tabs.Item>
              {/* <Tabs.Item
                active={location.pathname === "/client-overview"}
                onClick={() => window.open("/client-overview", "_blank")}
              >
                Client Overview
              </Tabs.Item> */}
              <Tabs.Item
                active={activeSection === "2b"}
                onClick={() => handleTabClick("2b")}
              >
                2B
              </Tabs.Item>
            </Tabs>
          </div>
        </div>

        {/* Dynamic Section Content with Routes */}
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start bg-default-background overflow-auto">
          <Routes>
            <Route index element={<Navigate to="reco" replace />} />
            {/* <Route index element={<Navigate to="data" replace />} /> */}
            <Route path="reco" element={<RecoSection />} />
            <Route path="data" element={<DataPage />} />
            <Route
              path="gl"
              element={<Navigate to="/dashboard/masters" replace />}
            />
            <Route path="masters" element={<MastersSection />} />
            <Route path="invoices" element={<InvoicesSection />} />
            <Route path="bills" element={<BillsSection />} />
            <Route path="credit" element={<CreditNotesSection />} />
            <Route path="debit" element={<DebitNotesSection />} />
            <Route path="cash-bank" element={<CashBankSection />} />
            <Route path="journal" element={<JournalSection />} />
            <Route path="queries" element={<QueriesSection />} />
            <Route path="compliance" element={<ComplianceSection />} />
            <Route path="approvals" element={<ApprovalsSection />} />
            <Route path="checklist" element={<ChecklistSection />} />
            <Route path="inbox" element={<InboxSection />} />
            <Route path="2b" element={<Gstr2bSection />} />
          </Routes>
        </div>
      </div>
    </DefaultPageLayout>
  );
};
